import React, { useMemo, useState } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Calendar } from 'shadcn/components/ui/Calendar';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';

// Parse date-only YYYY-MM-DD strings as local date at noon to avoid UTC midnight showing as previous day
export function parseDateValue(value) {
    if (value == null) return undefined;
    if (value instanceof Date) return value;
    if (typeof value !== 'string') return undefined;
    const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0, 0);
    }
    return new Date(value);
}

function DateRangeFilter({
    id,
    includeTime = false,
    label,
    onChange,
    placeholder = 'Pick a date',
    range = true,
    value,
}) {
    const [open, setOpen] = useState(false);
    const atLocalNoon = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0) : undefined);
    const pad = (n) => String(n).padStart(2, '0');
    const toInputValue = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        if (!includeTime) return `${year}-${month}-${day}`;
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const display = useMemo(() => {
        if (range) {
            if (value && value.startDate && value.endDate) {
                if (includeTime) {
                    const fmt = (d) => {
                        const date = parseDateValue(d);
                        return date
                            ? date.toLocaleString([], {
                                  year: 'numeric',
                                  month: 'numeric',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })
                            : '';
                    };
                    return `${fmt(value.startDate)} - ${fmt(value.endDate)}`;
                }
                return `${parseDateValue(value.startDate)?.toLocaleDateString() ?? ''} - ${parseDateValue(value.endDate)?.toLocaleDateString() ?? ''}`;
            }
            return placeholder || 'Pick a date range';
        }
        const single = value?.date ?? value;
        if (single) {
            const date = parseDateValue(single);
            if (date) {
                if (includeTime) {
                    return date.toLocaleString([], {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
                }
                return date.toLocaleDateString();
            }
        }
        return placeholder || 'Pick a date';
    }, [value, placeholder, range, includeTime]);

    const selected = useMemo(() => {
        if (range) {
            if (value && value.startDate && value.endDate) {
                return {
                    from: parseDateValue(value.startDate),
                    to: parseDateValue(value.endDate),
                };
            }
            return { from: undefined, to: undefined };
        }
        const single = value?.date ?? value;
        return single ? parseDateValue(single) : undefined;
    }, [value, range]);

    const startDateObj = value?.startDate ? parseDateValue(value.startDate) : undefined;
    const endDateObj = value?.endDate ? parseDateValue(value.endDate) : undefined;
    let singleDateObj;
    if (value?.date) {
        singleDateObj = parseDateValue(value.date);
    } else if (value && !value.startDate && !value.endDate) {
        singleDateObj = parseDateValue(value);
    } else {
        singleDateObj = undefined;
    }

    return (
        <div className="flex flex-col gap-1 min-w-[220px]">
            {label && <Label htmlFor={id}>{label}</Label>}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                        {display}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    {includeTime ? (
                        <div className="px-3 py-3">
                            {range ? (
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor={`${id}-start`}>Start</Label>
                                        <Input
                                            id={`${id}-start`}
                                            type="datetime-local"
                                            value={toInputValue(startDateObj)}
                                            onChange={(e) => {
                                                const picked = e.target.value ? new Date(e.target.value) : undefined;
                                                onChange({ startDate: picked, endDate: endDateObj });
                                            }}
                                            className="h-7 px-2 text-sm w-full"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor={`${id}-end`}>End</Label>
                                        <Input
                                            id={`${id}-end`}
                                            type="datetime-local"
                                            value={toInputValue(endDateObj)}
                                            onChange={(e) => {
                                                const picked = e.target.value ? new Date(e.target.value) : undefined;
                                                onChange({ startDate: startDateObj, endDate: picked });
                                            }}
                                            className="h-7 px-2 text-sm w-full"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    <Label htmlFor={id}>{label || 'Date'}</Label>
                                    <Input
                                        id={id}
                                        type="datetime-local"
                                        value={toInputValue(singleDateObj)}
                                        onChange={(e) => {
                                            const picked = e.target.value ? new Date(e.target.value) : undefined;
                                            onChange({ date: picked });
                                        }}
                                        className="h-7 px-2 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="p-3">
                            <Calendar
                                mode={range ? 'range' : 'single'}
                                selected={selected}
                                onSelect={(picked) => {
                                    if (range) {
                                        if (picked && picked.from && picked.to) {
                                            onChange({
                                                startDate: atLocalNoon(picked.from),
                                                endDate: atLocalNoon(picked.to),
                                            });
                                        }
                                    } else if (picked) {
                                        onChange({ date: atLocalNoon(picked) });
                                    }
                                }}
                                numberOfMonths={range ? 2 : 1}
                            />
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}

export { DateRangeFilter };

// Menu variant: renders input fields that open date pickers; selecting a date will not close the dropdown
function DateRangeFilterMenuItem({
    id,
    includeTime = false,
    label,
    onChange,
    placeholder = 'Pick a date', // eslint-disable-line no-unused-vars -- accepted for API; not rendered in menu variant
    range = true,
    value,
}) {
    const startLabel = 'Start Date';
    const endLabel = 'End Date';

    const startDateObj = value?.startDate ? parseDateValue(value.startDate) : undefined;
    const endDateObj = value?.endDate ? parseDateValue(value.endDate) : undefined;
    let singleDateObj;
    if (value?.date) {
        singleDateObj = parseDateValue(value.date);
    } else if (value && !value.startDate && !value.endDate) {
        singleDateObj = parseDateValue(value);
    } else {
        singleDateObj = undefined;
    }

    const pad = (n) => String(n).padStart(2, '0');
    const toInputValue = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        if (!includeTime) return `${year}-${month}-${day}`;
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const inputType = includeTime ? 'datetime-local' : 'date';
    const getPickedFromInput = (raw) => {
        if (!raw) return undefined;
        if (inputType === 'date') {
            const d = parseDateValue(raw);
            return d && !Number.isNaN(d.getTime()) ? d : undefined;
        }
        return new Date(raw);
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-0">
                <div className="px-2 py-2">
                    {range ? (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-start`}>{startLabel}</Label>
                                <Input
                                    id={`${id}-start`}
                                    type={inputType}
                                    value={toInputValue(startDateObj)}
                                    onChange={(e) => {
                                        const picked = getPickedFromInput(e.target.value);
                                        onChange({ startDate: picked, endDate: endDateObj });
                                    }}
                                    className="h-7 px-2 text-sm w-full"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-end`}>{endLabel}</Label>
                                <Input
                                    id={`${id}-end`}
                                    type={inputType}
                                    value={toInputValue(endDateObj)}
                                    onChange={(e) => {
                                        const picked = getPickedFromInput(e.target.value);
                                        onChange({ startDate: startDateObj, endDate: picked });
                                    }}
                                    className="h-7 px-2 text-sm w-full"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1">
                            <Label htmlFor={id}>{label}</Label>
                            <Input
                                id={id}
                                type={inputType}
                                value={toInputValue(singleDateObj)}
                                onChange={(e) => {
                                    const picked = getPickedFromInput(e.target.value);
                                    onChange({ date: picked });
                                }}
                                className="h-7 px-2 text-sm"
                            />
                        </div>
                    )}
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export { DateRangeFilterMenuItem };
